import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';

import styles from './Login.module.scss';
import { Text } from '@xarvio/corporate-styles';
import {
  isBarillaDomain,
  isPeroniDomain,
} from '@xarvio/frontend-core-state/lib/auth/utils/whitelabelDomain';
import { useResolvedDocUrl } from '@xarvio/frontend-core-state/lib/docs/useResolvedDocUrl';
import { userActions } from '@xarvio/frontend-core-state/lib/auth/actions';
import { PageLayoutAuth } from '@xarvio/frontend-core-components/components/PageLayoutAuth/PageLayoutAuth';
import { Column, Row, RowCell, Separator } from '@xarvio/layout';
import { InputControl } from '@xarvio/input';
import { Button } from '@xarvio/button';
import { CountryLanguageSwitcher } from '@xarvio/frontend-core-ui/lib/components/CountryLanguageSwitcher/CountryLanguageSwitcher';
import { FormProvider, useForm } from 'react-hook-form';
import { TranslateMessage } from '@xarvio/utils';
import { useIntl } from 'react-intl';
import {
  getAuthErrorSelector,
  getAuthOperationStatusSelector,
} from '@xarvio/frontend-core-state/lib/auth/selectors';
import { hasMultipleLanguages } from '@xarvio/frontend-core-state/lib/auth/utils/hasMultipleLanguages';
import { useIsFeatureEnabled } from '@xarvio/frontend-core-state/lib/featureFlags/useIsFeatureEnabled';
import intlSlice from '@xarvio/frontend-core-state/lib/intl/intlSlice';
import {
  countrySelector,
  languageSelector,
  localeSelector,
} from '@xarvio/frontend-core-state/lib/intl/selectors';
import { splitLocale } from '@xarvio/frontend-core-state/lib/intl/utils';
import { usePublicAuthUrls } from '../../routing/usePublicAuthUrls';
import { getPublicAuthUrls } from '../../routing/publicAuthUrls';
import { useUrlQuery } from '@xarvio/frontend-core-components/hooks/useUrlQuery';
import { AppThunkDispatch } from '@xarvio/frontend-core-state/lib/types';
import { getLoginSchema } from './utils';
import { FeatureToggle } from '@xarvio/frontend-core-ui/lib/components/FeatureToggle/FeatureToggle';
import { supportedCountries } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';
const OneSmartSprayLogo = React.lazy(() =>
  import('../../components/SmartSpray/OneSmartSprayLogo').then((m) => ({
    default: m.OneSmartSprayLogo,
  })),
);
import { useGetIsCountryEnabledToLogin } from '../../hooks/useGetIsCountryEnabledToLogin';
import { useWhitelabel } from '@xarvio/frontend-core-state/lib/auth/hooks/useWhitelabel/useWhitelabel';
import authSlice from '@xarvio/frontend-core-state/lib/auth/authSlice';
import { ToastType, useDispatchToast } from '@xarvio/toast';
import { CountryLanguageItem } from '@xarvio/frontend-core-ui/lib/components/CountryLanguageItem/CountryLanguageItem';
import { UsaTermsAndConditionsModal } from '@xarvio/frontend-core-ui/lib/components/UsaTermsAndConditionsModal/UsaTermsAndConditionsModal';
import { EmailInputField } from '@xarvio/frontend-core-ui/lib/components/AuthFields/EmailInputField';
import { getUserCountry } from '@xarvio/frontend-core-platform/lib/country/CountryUtils';

export const LoginPage: React.FC = () => {
  const intl = useIntl();
  const urlSearchParams = useUrlQuery();
  const history = useNavigate();
  const { registrationPageUrl, forgotPasswordPageUrl } = usePublicAuthUrls();
  const [
    isUsaTermsAndConditionModalVisible,
    setIsUsaTermsAndConditionModalVisible,
  ] = useState(false);
  const dispatchToast = useDispatchToast();
  const dispatchRedux = useDispatch<AppThunkDispatch>();
  const country = useSelector(countrySelector);
  const language = useSelector(languageSelector);
  const locale = useSelector(localeSelector);
  const tcDocUrl = useResolvedDocUrl('tc', locale);
  const privacyDocUrl = useResolvedDocUrl('privacy', locale);
  const shopAdditionalTcDocUrl = useResolvedDocUrl(
    'shop-additional-tc',
    locale,
  );
  const supportDocUrl = useResolvedDocUrl('support', locale);
  const faqDocUrl = useResolvedDocUrl('faq', locale);
  const imprintDocUrl = useResolvedDocUrl('imprint', locale);
  const kvkkDocUrl = useResolvedDocUrl('additional-tc', locale);
  const status = useSelector(getAuthOperationStatusSelector);
  const authError = useSelector(getAuthErrorSelector);
  const getIsCountryEnabledToLogin = useGetIsCountryEnabledToLogin();

  const isUserRegistrationEnabled = useIsFeatureEnabled('USER_REGISTRATION');

  const { isUsaUser, isJapanUser, isAustralianUser, isTurkishUser } =
    getUserCountry(country);

  const formMethods = useForm({
    resolver: yupResolver(getLoginSchema()),
    mode: 'onTouched',
  });
  const {
    handleSubmit,
    watch,
    formState: { isValid },
  } = formMethods;
  const email = watch('email');
  const password = watch('password');

  // Signal that the user intends to log in so the app can prefetch post-login bundles
  const prefetchSignalled = useRef(false);
  const signalPrefetch = useCallback(() => {
    if (!prefetchSignalled.current) {
      prefetchSignalled.current = true;
      window.dispatchEvent(new Event('prefetch-private-routes'));
    }
  }, []);

  useEffect(() => {
    if (authError.message) {
      dispatchToast({
        type: ToastType.DANGER,
        message: {
          title: intl.formatMessage({
            id: authError.message,
          }),
        },
      });
      dispatchRedux(authSlice.actions.resetAuthState());
    }
  }, [authError, dispatchToast, intl, dispatchRedux]);

  const login = () => {
    signalPrefetch();
    dispatchRedux(
      userActions.login(
        {
          email,
          password,
        },
        true,
      ),
    );
  };

  const onLocaleChange = (locale: string) => {
    const { country } = splitLocale(locale);
    dispatchRedux(intlSlice.actions.changeLocale(`${locale}`));
    const { loginPageUrl } = getPublicAuthUrls({ country, locale });
    history(`${loginPageUrl}?${urlSearchParams}`);
  };

  const supportedCountry = supportedCountries.find(
    (item) => item.locale === locale || item.countryCode === country,
  );
  const handleGoToRegistrationPage = () => {
    history(
      `${registrationPageUrl}${
        email ? `?email=${encodeURIComponent(email)}` : ''
      }`,
    );
  };
  const handleGoToForgotPasswordPage = () => {
    history(
      `${forgotPasswordPageUrl}${
        email ? `?email=${encodeURIComponent(email)}` : ''
      }`,
    );
  };

  const handleUsaTermsAndCondtionsModal = () =>
    setIsUsaTermsAndConditionModalVisible(!isUsaTermsAndConditionModalVisible);

  const handleOpenKvkkDoc = () => window.open(kvkkDocUrl, '_blank');

  const isBarillaDom = isBarillaDomain();
  const isPeroniDom = isPeroniDomain();
  const { whitelabelConfig } = useWhitelabel();

  const enabledCountries = useMemo(() => {
    if (
      !isBarillaDom &&
      !isPeroniDom &&
      whitelabelConfig?.supportedUserLocales
    ) {
      return supportedCountries.filter((country) =>
        whitelabelConfig?.supportedUserLocales?.includes(country.locale),
      );
    }

    return supportedCountries.filter((supportedCountry) =>
      getIsCountryEnabledToLogin(supportedCountry.countryCode),
    );
  }, [getIsCountryEnabledToLogin, isBarillaDom, isPeroniDom, whitelabelConfig]);

  const isShowingLoginTitle =
    !whitelabelConfig || !whitelabelConfig.loginHideTitle;
  const hideRegistrationButton =
    isBarillaDom || isPeroniDom || !isUserRegistrationEnabled;

  return (
    <PageLayoutAuth
      hasLogo
      country={country}
      bottomRightEl={
        isAustralianUser ? (
          <React.Suspense fallback={null}>
            <OneSmartSprayLogo />
          </React.Suspense>
        ) : undefined
      }
      {...(whitelabelConfig && { bgSrc: whitelabelConfig.authBgSrc })}
      customLogoEl={whitelabelConfig?.authLogoElem}
    >
      <Column minHeight={hideRegistrationButton ? undefined : 420}>
        <FormProvider {...formMethods}>
          <form
            name="form"
            onSubmit={handleSubmit(login)}
            onFocus={signalPrefetch}
          >
            {isShowingLoginTitle && (
              <Row>
                <RowCell>
                  <Text type={'h5'}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: intl.formatMessage({
                          id: isBarillaDom
                            ? 'component.LoginPage.title'
                            : 'component.LoginPage.nonBarilla.title',
                        }),
                      }}
                    />
                  </Text>
                </RowCell>
              </Row>
            )}

            <EmailInputField />

            <Row>
              <RowCell>
                <InputControl
                  name="password"
                  controlName={'password'}
                  withFormError
                  isPassword
                  width={300}
                  placeholder={intl.formatMessage({
                    id: 'component.RegistrationUserPassword.passwordPlaceholder',
                  })}
                  seleniumAttribute={'login-password-input'}
                  theme={'grey'}
                  tooltipPlacement="right"
                />
              </RowCell>
            </Row>
            <Row margin={'small'}>
              <RowCell>
                <Button
                  typeOperation={'submit'}
                  type={'primary'}
                  seleniumAttribute={'login-submit-button'}
                  label={<TranslateMessage id="component.LoginPage.signIn" />}
                  width={300}
                  isDisabled={!isValid}
                  isLoading={status === 'loading'}
                />
              </RowCell>
            </Row>
            <Row justify={'center'}>
              <RowCell auto>
                <Button
                  type={'secondary'}
                  label={
                    <TranslateMessage id="component.LoginPage.forgotPassword" />
                  }
                  size={'small'}
                  onClick={handleGoToForgotPasswordPage}
                  seleniumAttribute={'forgot-password-link'}
                />
              </RowCell>
            </Row>

            <Separator margin={'large'} transparent />

            {!hideRegistrationButton && (
              <Row>
                <RowCell>
                  <Button
                    type={'primary'}
                    label={
                      <TranslateMessage id="component.LoginPage.signUpForFree" />
                    }
                    width={300}
                    onClick={handleGoToRegistrationPage}
                    seleniumAttribute={'registration-link'}
                  />
                </RowCell>
              </Row>
            )}

            <Separator margin={'small'} transparent />

            <Row justify={'center'}>
              <RowCell auto>
                <CountryLanguageSwitcher
                  country={country}
                  language={language}
                  onLocaleChange={onLocaleChange}
                  enabledCountries={enabledCountries}
                  button={
                    supportedCountry ? (
                      <div>
                        <CountryLanguageItem
                          showLanguage={hasMultipleLanguages(
                            supportedCountry.countryCode,
                          )}
                          size={'medium'}
                          countryTranslationKey={
                            supportedCountry.countryTranslationKey
                          }
                          languageTranslationKey={
                            supportedCountry.languageTranslationKey
                          }
                          countryCode={country}
                          countryClassName={styles.country}
                        />
                      </div>
                    ) : (
                      <Button
                        type={'secondary'}
                        label={<TranslateMessage id="general.change" />}
                        size={'small'}
                      />
                    )
                  }
                />
              </RowCell>
            </Row>

            {isUsaUser && (
              <Row margin={'smaller'}>
                <RowCell align={'center'}>
                  <TranslateMessage id="component.LoginPage.weCollectData" />
                  <Button
                    onClick={handleUsaTermsAndCondtionsModal}
                    type="secondary"
                    size="medium"
                    label={<TranslateMessage id="component.LoginPage.ccpa" />}
                  />
                </RowCell>
              </Row>
            )}

            {isTurkishUser && (
              <Row margin={'smaller'}>
                <RowCell align={'center'}>
                  <Button
                    onClick={handleOpenKvkkDoc}
                    type="secondary"
                    size="medium"
                    label={<TranslateMessage id="component.LoginPage.kvkk" />}
                  />
                </RowCell>
              </Row>
            )}

            {(!whitelabelConfig ||
              !whitelabelConfig.authHideBASFAttribution) && (
              <Row margin={'smaller'}>
                <RowCell align={'center'}>
                  <TranslateMessage
                    tag="btn2"
                    id="component.LoginPage.companyInfo"
                    className={styles.footerCopyright}
                  />
                </RowCell>
              </Row>
            )}

            <Row>
              <RowCell align={'center'}>
                <Text type={'btn2'} className={styles.footerLinks}>
                  <a
                    href={tcDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-selenium="footer-tc-link"
                  >
                    <TranslateMessage id="general.termsAndConditions" />
                  </a>
                  {' | '}
                  <a
                    href={privacyDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-selenium="footer-privacy-link"
                  >
                    <TranslateMessage id="component.UserSettingsPage.privacyPolicy" />
                  </a>
                  {' | '}
                  {isJapanUser && (
                    <>
                      <a
                        href={shopAdditionalTcDocUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-selenium="footer-adyen-shop-link"
                      >
                        <TranslateMessage id="component.UserSettingsPage.notation" />
                      </a>
                      {' | '}
                    </>
                  )}
                  <FeatureToggle featureName="SUPPORT">
                    <a
                      href={supportDocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-selenium="footer-support-link"
                    >
                      <TranslateMessage id="component.UserSettingsPage.support" />
                    </a>
                    {' | '}
                  </FeatureToggle>
                  <FeatureToggle featureName="FAQ">
                    <a
                      href={faqDocUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-selenium="footer-faq-link"
                    >
                      <TranslateMessage id="general.faq" />
                    </a>
                    {' | '}
                  </FeatureToggle>
                  <a
                    href={imprintDocUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-selenium="footer-imprint-link"
                  >
                    <TranslateMessage id="component.UserSettingsPage.imprint" />
                  </a>
                </Text>
              </RowCell>
            </Row>
          </form>
        </FormProvider>
      </Column>
      {isUsaTermsAndConditionModalVisible && (
        <UsaTermsAndConditionsModal onClose={handleUsaTermsAndCondtionsModal} />
      )}
    </PageLayoutAuth>
  );
};
